import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faFolderMinus, faFolderPlus } from '@fortawesome/free-solid-svg-icons';
import './Treestructure.css';

const Treestructure = ({ node, onNodeClick, selectedPath, isFlat = false }) => {
  const [expanded, setExpanded] = useState(false);
  const itemRef = useRef(null);

  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isSelected = node.path === selectedPath;

  const depth = useMemo(() => {
    if (isFlat) return 0;
    if (!node?.path) return 0;
    const normalized = String(node.path)
    .replace(/^[\\/]+/, '')
    .replace(/[\\/]+$/, '');  
    return normalized.split(/[\\/]/).length - 1;
  }, [node?.path]);

  // --- helpers to normalize and test ancestry ---
  const normalize = useCallback((p) => {
    if (!p) return '';
    const s = String(p).replace(/\\/g, '/').replace(/\/+/g, '/').replace(/(^\/|\/$)/g, '');
    return s;
  }, []);

  const isAncestorOrSelf = useMemo(() => {
    const n = normalize(node?.path);
    const s = normalize(selectedPath);
    if (!n) return s === '';          
    if (s === n) return true;
    return s.startsWith(n + '/');      
  }, [node?.path, selectedPath, normalize]);

  // Auto-expand whenever the selected path is within this subtree
  useEffect(() => {
    if (hasChildren) setExpanded(isAncestorOrSelf);
  }, [hasChildren, isAncestorOrSelf]);

  // Keep the selected item in view
  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isSelected]);



  const handleSelect = useCallback(() => {
    if (onNodeClick) onNodeClick(node);
  }, [onNodeClick, node]);

  const toggleExpand = useCallback((e) => {
    e.stopPropagation();

    handleSelect();

    if (hasChildren) {
      setExpanded(prev => !prev);
    }
  }, [hasChildren, handleSelect]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    } else if (e.key === 'ArrowRight' && hasChildren) {
      e.preventDefault();
      setExpanded(true);
    } else if (e.key === 'ArrowLeft' && hasChildren) {
      e.preventDefault();
      setExpanded(false);
    }
  }, [handleSelect, hasChildren]);

  const folderIcon = hasChildren ? (expanded ? faFolderMinus : faFolderPlus) : faFolder;

  return (
    <div className="tree-container my-1" role={depth === 0 ? 'tree' : undefined}>
      <div
        className={`tree-node${isSelected ? ' selected' : ''}`}
        role="treeitem"
        aria-expanded={hasChildren ? expanded : undefined}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={handleSelect}
        style={{ paddingLeft: depth * 16 }}
        ref={itemRef}
      >
        <button
          type="button"
          className="tree-folder-btn"
          onClick={toggleExpand}
          aria-label={expanded ? 'Collapse folder' : 'Expand folder'}
          aria-expanded={hasChildren ? expanded : undefined}
          title={expanded ? 'Collapse' : 'Expand'}
          tabIndex={-1}
        >
          <FontAwesomeIcon icon={folderIcon} className="tree-icon" />
        </button>
        <span className="tree-label">{node.name}</span>
      </div>

      {hasChildren && expanded && (
        <div className="tree-children" role="group">
          {node.children.map((child, index) => (
            <Treestructure
              key={child.path || `${node.path || ''}-${index}`}
              node={child}
              onNodeClick={onNodeClick}
              selectedPath={selectedPath}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Treestructure;
